import { LiquidVariables, Event, CustomFieldDefinition } from "../models/Event";
import { buildDeclarativeIdString } from "./StringUtils";
import logger from "./logging";

export type LiquidLocalizedVariable = {
    id: string;
    name: string;
    example: string;
    priority: number;
    type: "EVENT" | "GUEST" | "GUEST_FIELD" | "OTHER";
};

/**
 * Get priority for liquid variable ordering
 * Higher priority = appears first in the list
 */
const getPriority = (variableId: string): number => {
    switch (variableId) {
        // Example: High priority variables appear first
        case "guest.fullName":
            return 199;
        case "guest.firstName":
            return 198;
        case "guest.lastName":
            return 197;
        case "event.name":
            return 99;
        case "event.start.date":
            return 98;
        case "event.start.time":
            return 97;
        case "event.end.date":
            return 96;
        case "event.end.time":
            return 95;
        // Add more priorities as needed
        default:
            return 0; // Default priority
    }
};

/**
 * Get localized name for liquid variable
 * Falls back to variable ID if no translation exists
 */
const getLocalizedName = (
    variableId: string,
    customFieldDefinitions?: CustomFieldDefinition[],
    t?: (key: string) => string,
): string => {
    if (!t) return variableId;

    if (customFieldDefinitions && variableId.startsWith("guest.fields.")) {
        const declarativeId = variableId.slice("guest.fields.".length); // part after guest.fields.
        const matchedField = customFieldDefinitions.find(
            (field) => buildDeclarativeIdString(field.name) == declarativeId,
        );
        if (matchedField) {
            return matchedField.name;
        } else {
            logger.warn(`No matched field for ${variableId}`);
        }
    }

    try {
        // Try to get localized name from translations using nested object path
        // Convert "event.start.date" -> "liquid:liquidVariables.event.start.date"
        const translationKey = `liquid:liquidVariables.${variableId}`;
        const translated = t(translationKey);

        // If no translation key was found, return the variable ID
        return translated === translationKey ? variableId : translated;
    } catch (error) {
        // Fallback to variable ID if translation fails
        return variableId;
    }
};

/**
 * Determine variable type based on its ID prefix
 */
const getVariableType = (variableId: string): "EVENT" | "GUEST" | "GUEST_FIELD" | "OTHER" => {
    if (variableId.startsWith("event.")) return "EVENT";
    if (variableId.startsWith("guest.field")) return "GUEST_FIELD";
    if (variableId.startsWith("guest.")) return "GUEST";
    return "OTHER";
};

export const mapLiquidVariables = (
    liquidVariables: LiquidVariables,
    event: Event | null,
    t?: (key: string) => string,
): LiquidLocalizedVariable[] => {
    // Convert liquid variables object to array with metadata, preserving the original order
    const returnValues = Object.entries(liquidVariables).map(([key, value], originalIndex) => {
        return {
            id: key,
            name: getLocalizedName(key, event?.customFieldDefinitions, t),
            example: String(value),
            priority: getPriority(key),
            type: getVariableType(key),
            originalIndex, // Store original position for stable sorting
        };
    });

    // Sort by priority (highest first), then by original array order
    return returnValues
        .sort((a, b) => {
            if (a.priority !== b.priority) {
                return b.priority - a.priority; // Higher priority first
            }
            return a.originalIndex - b.originalIndex; // Original order fallback
        })
        .map(({ originalIndex, ...item }) => item); // Remove originalIndex from final result
};
